import { ISchemeSpecificLoginDetailsForm } from "../interfaces/IHealthCare";
import { IScheme } from "../interfaces/IScheme";
import {
  InsurancePortalUse,
  InsuranceSetupFormValues,
  InsuranceSetupMeta,
  InsuranceSetupRow,
  InsuranceSetupStatus,
} from "../interfaces/IInsuranceSetup";

const META_PREFIX = "IS_META:";

const defaultMeta = (): InsuranceSetupMeta => ({
  portalUse: "WEBSITE",
  tpaAvailable: false,
  status: "ACTIVE",
  tariffFileName: null,
  tariffFileUrl: null,
});

export function parseInsuranceSetupMeta(
  alternateName?: string | null
): InsuranceSetupMeta {
  const base = defaultMeta();
  if (!alternateName || typeof alternateName !== "string") return base;

  if (alternateName.startsWith(META_PREFIX)) {
    try {
      const parsed = JSON.parse(alternateName.slice(META_PREFIX.length));
      return {
        portalUse: normalizePortalUse(parsed?.portalUse) || base.portalUse,
        tpaAvailable: Boolean(parsed?.tpaAvailable),
        status:
          String(parsed?.status || "").toUpperCase() === "INACTIVE"
            ? "INACTIVE"
            : "ACTIVE",
        tariffFileName: parsed?.tariffFileName || null,
        tariffFileUrl: parsed?.tariffFileUrl || null,
      };
    } catch {
      return base;
    }
  }

  // Legacy / loose keys in alternateName
  if (/portalUse\s*=\s*EMAIL/i.test(alternateName)) base.portalUse = "EMAIL";
  if (/portalUse\s*=\s*IHX/i.test(alternateName)) base.portalUse = "IHX";
  if (/portalUse\s*=\s*WEBSITE/i.test(alternateName)) base.portalUse = "WEBSITE";
  if (/status\s*=\s*INACTIVE/i.test(alternateName)) base.status = "INACTIVE";
  if (/tpa\s*=\s*YES/i.test(alternateName)) base.tpaAvailable = true;

  return base;
}

export function encodeInsuranceSetupMeta(meta: InsuranceSetupMeta): string {
  return `${META_PREFIX}${JSON.stringify({
    portalUse: meta.portalUse,
    tpaAvailable: meta.tpaAvailable,
    status: meta.status,
    tariffFileName: meta.tariffFileName,
    tariffFileUrl: meta.tariffFileUrl,
  })}`;
}

function normalizePortalUse(value: unknown): InsurancePortalUse | null {
  const v = String(value || "")
    .trim()
    .toUpperCase();
  if (v === "IHX" || v === "WEBSITE" || v === "EMAIL") return v;
  return null;
}

export function derivePortalUse(args: {
  metaPortalUse?: InsurancePortalUse | null;
  loginUrl?: string | null;
  schemeName?: string | null;
}): InsurancePortalUse {
  if (args.metaPortalUse) return args.metaPortalUse;
  const name = (args.schemeName || "").toUpperCase();
  if (name.includes("IHX") || name.includes("HDFC ERGO")) return "IHX";
  if (args.loginUrl) return "WEBSITE";
  return "EMAIL";
}

export function mapMisCredentialToInsuranceSetupRow(
  cred: ISchemeSpecificLoginDetailsForm,
  scheme?: IScheme | null
): InsuranceSetupRow {
  const meta = parseInsuranceSetupMeta(cred.alternateName);
  const insuranceCompany =
    scheme?.alternateName || scheme?.schemeName || `Scheme #${cred.schemeId}`;
  const portalUse = derivePortalUse({
    metaPortalUse: meta.portalUse,
    loginUrl: cred.loginUrl,
    schemeName: scheme?.schemeName,
  });

  return {
    id: Number(cred.id),
    healthCareId: Number(cred.healthCareId),
    schemeId: Number(cred.schemeId),
    insuranceCompany: String(insuranceCompany),
    portalUse,
    tpaAvailable: meta.tpaAvailable,
    userName: cred.userName || null,
    passwordConfigured: Boolean(cred.password),
    portalUrl: cred.loginUrl || null,
    tariffFileName: meta.tariffFileName,
    tariffFileUrl: meta.tariffFileUrl,
    status: meta.status,
    lastUpdated: cred.modifiedAt || cred.createdAt || null,
    createdAt: cred.createdAt || null,
    rawPassword: cred.password || null,
    alternateName: cred.alternateName || null,
  };
}

export function mapFormToMisCredentialPayload(
  form: InsuranceSetupFormValues,
  existing?: ISchemeSpecificLoginDetailsForm | null
): ISchemeSpecificLoginDetailsForm {
  const meta: InsuranceSetupMeta = {
    portalUse: form.portalUse,
    tpaAvailable: form.tpaAvailable,
    status: form.status,
    tariffFileName: form.tariffFileName,
    tariffFileUrl: form.tariffFileUrl,
  };

  return {
    id: existing?.id ?? null,
    healthCareId: existing?.healthCareId ?? null,
    schemeId: form.schemeId,
    loginUrl: form.portalUrl || null,
    userName: form.userName || null,
    password: form.password || existing?.password || null,
    alternateName: encodeInsuranceSetupMeta(meta),
  };
}

export function rowToFormValues(row: InsuranceSetupRow): InsuranceSetupFormValues {
  return {
    schemeId: row.schemeId,
    tpaAvailable: row.tpaAvailable,
    portalUse: row.portalUse,
    userName: row.userName || "",
    password: "",
    portalUrl: row.portalUrl,
    tariffFileName: row.tariffFileName,
    tariffFileUrl: row.tariffFileUrl,
    status: row.status as InsuranceSetupStatus,
  };
}
